import React from 'react';

import styled from '@emotion/styled';
import {Color} from '@nfq/colors';
import {Spacer, spacing} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';

import {Close} from '../../icons';
import {P} from '../Text/Text';

import {useFilterTags} from './useFilterTags';

import type {FilterConfig} from '../FilterWrapper/FilterWrapper';

/**
 * The `ComponentProps` interface defines the shape of the properties object that is expected for this component.
 * It outlines the required properties that needs to be provided when utilizing this component expecting an object of this type.
 */
export interface ComponentProps {
    /**
     * The `filter` property contains an array of filter configuration objects that define the available filters for the component.
     * Each FilterConfig object in the array represents a specific filter option with its configuration settings and metadata.
     * This property is essential for determining which filter tags should be displayed and how they should behave within the component.
     */
    filter: FilterConfig[];
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'FilterTags'
     */
    testId?: string;
}

/**
 * The FilterTags component displays a collection of active filter tags with the ability to remove individual filters or reset all filters at once.
 * This component observes changes in the filter state using MobX and renders clickable tags that allow users to manage their applied filters.
 * Each tag shows the filter label and includes a close icon for removal, with an additional "reset all" button when multiple filters are active.
 *
 * @param props        The component props.
 * @param props.filter An array of filter configuration objects that define the available filters for the component.
 * @param props.testId A unique identifier for testing purposes, used to locate the component during automated tests.
 * @returns The FilterTags component JSX containing the rendered filter tags and reset functionality.
 *
 * @example
 * ```tsx
 * <FilterTags
 *     filter={[
 *         { name: 'category', value: 'design', label: 'Design' },
 *         { name: 'status', value: 'active', label: 'Active' }
 *     ]}
 *     testId="product-filters"
 * />
 * ```
 */
const FilterTags = observer(({filter, testId = 'FilterTags'}: ComponentProps) => {
    const {colors, handleClick, handleResetAll, mappedTags} = useFilterTags(filter);

    return (
        <>
            {mappedTags.length !== 0 && (
                <Spacer y={4} />
            )}
            <Wrapper data-cy={testId}>
                {mappedTags.map(i => (
                    <Tag
                        key={`${i.name}-${i.value}`}
                        data-name={i.name}
                        data-value={i.value}
                        type="button"
                        onClick={handleClick}
                    >
                        <P>{i.label}</P>
                        {i.isDeletable && <Close color1={colors.textPrimary} height={16} width={16} />}
                    </Tag>
                ))}
                {mappedTags.length > 0 && mappedTags.some(v => v.isDeletable) && (
                    <ResetTag type="button" onClick={handleResetAll}>
                        <P>
                            <Color $color={colors.neutral0}>
                                Alle zurücksetzen
                            </Color>
                        </P>
                    </ResetTag>
                )}
            </Wrapper>
        </>
    );
});

FilterTags.displayName = 'FilterTags';

export {FilterTags};

const Wrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing(2)};
`;

const Tag = styled.button`
    align-items: center;
    background-color: ${({theme}) => theme.colors.neutral0};
    border: 1px solid ${({theme}) => theme.colors.neutral40};
    border-radius: 24px;
    cursor: pointer;
    display: flex;
    gap: ${spacing(1)};
    padding: ${spacing(1.5)} ${spacing(3)};
    width: fit-content;
`;

const ResetTag = styled(Tag)`
    background-color: ${({theme}) => theme.colors.primary};
    border: 1px solid ${({theme}) => theme.colors.primary};
    transition: border 0.3s 0s ease-in-out, background-color 0.3s 0s ease-in-out;

    &:hover {
        background-color: ${({theme}) => theme.colors.primaryDark};
        border-color: ${({theme}) => theme.colors.primaryDark};
        transition: border 0.3s 0s ease-in-out, background-color 0.3s 0s ease-in-out;
    }
`;